// What a coffee's label says, as text: the tidying the synced catalogue needs
// (glued labels, shouting, "[FILTER]" prefixes), the spec list and the
// roaster's story. One source for every place a label is printed or read:
// the Label sheet (CoffeeLabel.vue), the native "Walk the shelf" card
// (src/lib/shelf-ar.js) and the printed back of the bag (src/lib/front-png.js),
// so the words match everywhere.

// The synced catalogue scrapes the product page's text, so a field sometimes
// arrives with the NEXT label glued on ("CaturraProcess:", "1,600mRoast:"),
// shouting in caps, or ending in a stray comma. Tidy it; never invent.
const GLUED = /(Process|Roast|Region|Producer|Variety|Altitude|Farm|Elevation)\s*:?\s*$/i;
export function tidy (v) {
  if (v == null) return '';
  let s = String(v).trim().replace(GLUED, '').replace(/[\s·,;.]+$/, '').trim();
  if (!s || /^mixed$/i.test(s)) return s ? 'Mixed' : '';
  if (s === s.toUpperCase() && /[A-Z]{3}/.test(s)) {
    s = s.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase()).replace(/\bSl(\d)/g, 'SL$1');
  }
  return s;
}

/** "1450-1900 MASL" → "1,450–1,900 masl". Falls back to the tidied text. */
export function tidyAltitude (v) {
  const s = tidy(v);
  const nums = (s.match(/\d[\d,]*/g) || []).map((n) => Number(n.replace(/,/g, ''))).filter((n) => n >= 100 && n <= 3000);
  if (!nums.length) return s;
  const f = (n) => n.toLocaleString('en-AU');
  return nums.length > 1 ? `${f(nums[0])}–${f(nums[nums.length - 1])} masl` : `${f(nums[0])} masl`;
}

export const titleWords = (t) => t.toLowerCase().replace(/(^|[\s(/-])([a-z])/g, (m, a, b) => a + b.toUpperCase())
  .replace(/\b(Aa|Aaa|Ab|Jarc|Ea|Co2)\b/g, (w) => w.toUpperCase()).replace(/\b(Sl|G)(\d)/g, (m, a, d) => a.toUpperCase() + d).replace(/\b(\d+)(G|Kg)\b/g, (m, n, u) => n + u.toLowerCase());

/**
 * A coffee's name as a person would write it. The shop's titles sometimes
 * carry a "[FILTER]" prefix (the Brew spec already says so) and shout in
 * capitals; the words are kept, only the casing and the prefix change.
 */
export function displayName (name) {
  let s = String(name || '').replace(/^\s*\[[^\]]{2,20}\]\s*/, '').trim();
  const letters = s.replace(/[^A-Za-z]/g, '');
  if (letters.length > 4 && letters === letters.toUpperCase()) s = titleWords(s);
  else s = s.replace(/\b[A-Z]{4,}\b/g, (w) => (/^(SL|AA)/.test(w) ? w : titleWords(w)));
  return s;
}

/** A shouted story line ("SHIPPING MID NOVEMBER*") in sentence case. */
export const calm = (t) => {
  const letters = t.replace(/[^A-Za-z]/g, '');
  if (letters.length < 6 || letters !== letters.toUpperCase()) return t;
  const l = t.toLowerCase();
  return l.charAt(0).toUpperCase() + l.slice(1);
};

const hasWords = (t) => /[A-Za-z]{2}/.test(t);
export const sentence = (s) => { const t = String(s).trim(); return t.charAt(0).toUpperCase() + t.slice(1); };

/** Tasting notes in sentence case. */
export const labelNotes = (c) => (c?.notes || []).map(sentence).filter(Boolean);

/**
 * The spec list, in label order: [{ label, value, scan, wide }].
 * Empty fields are left out, never shown as "—".
 */
export function labelSpecs (c) {
  const out = [];
  const origin = (c?.origins || []).join(', ');
  const region = tidy(c?.region);
  if (origin || region) out.push({ label: 'Origin', value: [origin, region].filter(Boolean).join(' · '), scan: '' });
  const process = (c?.process || []).map(tidy).filter(Boolean).join(' · ');
  if (process) out.push({ label: 'Process', value: process, scan: process });
  const variety = tidy(c?.variety);
  if (variety) out.push({ label: 'Variety', value: variety, scan: variety, wide: variety.length > 22 });
  const altitude = tidyAltitude(c?.altitude);
  if (altitude) out.push({ label: 'Altitude', value: altitude, scan: '' });
  const producer = tidy(c?.producer);
  if (producer) out.push({ label: 'Producer', value: producer, scan: '' });
  if (c?.brew) out.push({ label: 'Brew', value: c.brew, scan: c.kind === 'Decaf' ? `${c.brew} decaf` : c.brew });
  // An odd count leaves a hole at the end of a two-column grid: let the last one span.
  const narrow = out.filter((s) => !s.wide);
  if (narrow.length % 2 === 1) narrow[narrow.length - 1].wide = true;
  return out;
}

const FACT = /^([A-Z][A-Z &/-]{2,24}):\s*(.+)$/;
// The roaster's own "Tasting notes" block repeats "Tastes like": drop the
// heading and the list line under it.
const TASTING = /^\s*tasting notes?\s*:?\s*$/i;
const withoutTasting = (lines) => lines.filter((t, i) => !TASTING.test(t) && !(i > 0 && TASTING.test(lines[i - 1]) && t.length < 90));

/**
 * The roaster's story as paragraphs: [{ key, text, notice }]. `key` is set for
 * a "KEY: value" fact line; `notice` for a short shouted sub-heading.
 */
export function labelStory (c) {
  return withoutTasting((c?.story || []).filter(hasWords)).slice(0, 8).map((p) => {
    const m = String(p).match(FACT);
    if (m && /^tasting notes?$/i.test(m[1].trim())) return null;
    if (m) return { key: sentence(m[1].toLowerCase()), text: m[2], notice: false };
    const t = String(p);
    // A short shouted line is a notice ("Shipping mid November*"), not prose.
    const notice = t.length < 48 && t.replace(/[^A-Za-z]/g, '') === t.replace(/[^A-Za-z]/g, '').toUpperCase();
    // Title case keeps the place names in "KENYA, GACHATHA FACTORY, NYERI REGION".
    return { key: '', text: notice ? titleWords(t) : calm(t), notice };
  }).filter(Boolean);
}

/**
 * A short excerpt of the story for print: the first prose paragraphs, cut to
 * whole sentences, at most `sentences` of them and about `max` characters.
 */
export function storyExcerpt (c, { sentences = 3, max = 260 } = {}) {
  const prose = labelStory(c).filter((p) => !p.key && !p.notice).map((p) => p.text);
  const all = prose.join(' ').replace(/\s+/g, ' ').trim();
  if (!all) return '';
  const parts = all.match(/[^.!?]+[.!?]+["’”)]*|[^.!?]+$/g) || [all];
  let out = '';
  for (const s of parts.slice(0, sentences)) {
    const next = (out ? `${out} ` : '') + s.trim();
    if (out && next.length > max) break;
    out = next;
  }
  if (out.length > max + 40) out = `${out.slice(0, max).replace(/\s+\S*$/, '')}…`;
  return out;
}
